import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import classNames from 'classnames';
import { CmsImageSchema, CmsNumberSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import type { ContentComponent } from '$cms/ContentComponent';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { VrmLookupModal } from '$quantum/molecules/VrmLookupModal';
import { useAppContext } from '$util/AppContext';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { Hero24 } from '@/components/molecules/Hero24';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import styles from './index.module.scss';

const defaultConfiguration = {
  sku: undefined,
};

const CmsHero24Schema = {
  translatedEntities: z.object({
    heading: z.union([CmsRichTextSchema, CmsShortTextSchema]),
    postcodeLabel: CmsShortTextSchema,
    vrmLabel: CmsShortTextSchema,
    mainCta: CmsShortTextSchema,
    invalidPostcodeMessage: CmsShortTextSchema,
    invalidVRMMessage: CmsShortTextSchema,
    missingPostcodeMessage: CmsShortTextSchema,
    missingVRMMessage: CmsShortTextSchema,
    googleReviewsStars: CmsNumberSchema.optional(),
    googleReviewsTotalReviews: CmsNumberSchema.optional(),
    googleReviewsLink: CmsShortTextSchema.optional(),
    googleReviewsReviewsText: CmsShortTextSchema.optional(),
    googleReviewsBasedOnText: CmsShortTextSchema.optional(),
    googleReviewsLogo: CmsImageSchema.optional(),
    price: CmsShortTextSchema.optional(),
  }),
  configuration: z
    .object({
      sku: z.string().optional(),
    })
    .default(defaultConfiguration),
};

export type CmsHero24TranslatedEntities = SchemaType<(typeof CmsHero24Schema)['translatedEntities']>;
export type CmsHero24Configuration = SchemaType<(typeof CmsHero24Schema)['configuration']>;

const toggleNavBg = (entries: IntersectionObserverEntry[], topNavBarEl: HTMLElement) => {
  entries.forEach((entry: IntersectionObserverEntry) => {
    if (window.innerWidth >= 600) {
      if (entry.intersectionRatio < 1) {
        // make nav opaque
        topNavBarEl.classList.remove('transparent');
      } else {
        // make nav transparent
        topNavBarEl.classList.add('transparent');
      }
    }
  });
};

const stickyButton = (observedTarget: IntersectionObserverEntry, setState: (state: boolean) => void) => {
  const { isIntersecting } = observedTarget;
  if (window.scrollY === 0) {
    setState(false);
  } else {
    setState(!isIntersecting);
  }
};

const makeTranslationProps = (translatedEntities: CmsHero24TranslatedEntities) => {
  return {
    heading:
      'content' in translatedEntities.heading
        ? renderRichTextContent(translatedEntities.heading.content)
        : translatedEntities.heading.value,
    postcodeLabel: translatedEntities.postcodeLabel.value,
    vrmLabel: translatedEntities.vrmLabel.value,
    mainCta: translatedEntities.mainCta.value,
    invalidPostcodeMessage: translatedEntities.invalidPostcodeMessage.value,
    invalidVRMMessage: translatedEntities.invalidVRMMessage.value,
    missingPostcodeMessage: translatedEntities.missingPostcodeMessage.value,
    missingVRMMessage: translatedEntities.missingVRMMessage.value,
    googleReviewsStars: translatedEntities.googleReviewsStars?.number,
    googleReviewsTotalReviews: translatedEntities.googleReviewsTotalReviews?.number,
    googleReviewsLink: translatedEntities.googleReviewsLink?.value,
    googleReviewsReviewsText: translatedEntities.googleReviewsReviewsText?.value,
    googleReviewsBasedOnText: translatedEntities.googleReviewsBasedOnText?.value,
    googleReviewsLogo: translatedEntities.googleReviewsLogo,
    price: translatedEntities.price?.value,
  };
};

export const CmsHero24: ContentComponent<CmsHero24TranslatedEntities, CmsHero24Configuration> =
  function CmsHero24({ translatedEntities, configuration, className }): JSX.Element {
    const { campaign, locale } = useAppContext<AppContextGlobal>();

    const cmsPageContext = useCmsPageContext();
    let skuFromDynamicPage = '';
    const cmsContextSku = cmsPageContext?.pageRoutes?.[0]?.collectionItems?.product?.sku;
    if (cmsContextSku) {
      skuFromDynamicPage = cmsContextSku;
    }

    const headingElement = useRef<HTMLHeadingElement>(null);
    const heroVrmElement = useRef<HTMLDivElement>(null);

    const [showStickyButton, setShowStickyButton] = useState(false);

    const [stickyButtonIntersectionObserver, setStickyButtonIntersectionObserver] = useState<
      IntersectionObserver | undefined
    >(undefined);

    const [topNavOpacityIntersectionObserver, setTopNavOpacityIntersectionObserver] = useState<
      IntersectionObserver | undefined
    >(undefined);

    useEffect(() => {
      if (campaign || !headingElement.current) return undefined;

      const contentSectionEl = document.querySelector<HTMLElement>('.ContentSectionContainer');
      const topNavBarEl = document.querySelector<HTMLElement>('.TopNavigationBar');

      if (!contentSectionEl || !topNavBarEl) return undefined;

      const isTop =
        (className || '').split(' ').includes('top') &&
        contentSectionEl.previousElementSibling === topNavBarEl.parentElement;

      if (!isTop) {
        contentSectionEl.firstElementChild?.classList.remove('top');
        return undefined;
      }

      topNavBarEl.classList.add('transparent');
      topNavBarEl.classList.add('transition');

      const observerTarget = headingElement.current;

      const observer = new IntersectionObserver((entries) => toggleNavBg(entries, topNavBarEl), {
        rootMargin: '-80px 0px 0px',
        threshold: [1],
      });

      observer.observe(observerTarget);

      setTopNavOpacityIntersectionObserver(observer);

      return () => {
        observer.unobserve(observerTarget);
      };
    }, [headingElement, campaign, className]);

    useEffect(() => {
      if (heroVrmElement.current) {
        const target = heroVrmElement.current;
        const observer = new IntersectionObserver(
          ([observedTarget]) => stickyButton(observedTarget, setShowStickyButton),
          {
            threshold: [0],
          }
        );

        observer.observe(target);

        setStickyButtonIntersectionObserver(observer);

        return () => {
          observer.unobserve(target);
        };
      }
      return () => undefined; // needed due to consistent return lint rule
    }, [heroVrmElement, setStickyButtonIntersectionObserver, setShowStickyButton]);

    useResizeViewport({
      breakpoint: 'small',
      direction: 'up',
      fn: (isDesktop) => {
        // @NOTE if desktop, don't show
        if (heroVrmElement?.current && headingElement?.current) {
          if (isDesktop) {
            stickyButtonIntersectionObserver?.unobserve(heroVrmElement.current);
            topNavOpacityIntersectionObserver?.observe(headingElement.current);
            // setShowStickyButton(false);
          } else {
            stickyButtonIntersectionObserver?.observe(heroVrmElement.current);
            topNavOpacityIntersectionObserver?.unobserve(headingElement.current);
          }
        }
      },
    });

    return (
      <>
        <Hero24
          className={classNames(styles.topAdjustment, className)}
          {...{
            ...makeTranslationProps(translatedEntities),
            sku: configuration?.sku || skuFromDynamicPage,
            vrmRef: heroVrmElement,
            headingRef: headingElement,
            locale,
          }}
        />
        {showStickyButton && (
          <div className={classNames(`${CmsHero24.displayName}StickyCta`, styles.stickyCTA)}>
            <VrmLookupModal
              fullWidth
              id="VrmLookupModalButton"
              toggleButton={{
                size: 'L',
                children: translatedEntities.mainCta?.value || 'Get an instant quote',
              }}
            />
          </div>
        )}
      </>
    );
  };
CmsHero24.displayName = 'CmsHero24';
CmsHero24.schema = CmsHero24Schema;
